'use client';

import { useEffect, useRef } from 'react';
import { cn, getColorway, getYMargins } from '@/utils/styles';

import { AddCircle, RemoveCircle } from '@1debit/icons';
import { liveUpdateAttrs } from '@/libs/contentful/utils';

import CTA from '@/components/shared/CTA/CTA';
import Media from '@/components/shared/Media/Media';
import { CardsVerticalScrollItemMobileProps } from './types';
import { useExpandableCardsItemToggleTracking } from '@/components/analytics/hooks';
import { trackExpandableCardItemToggled } from '@/components/analytics/eventHandlerTracking';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';
import RichTextList from '@/components/shared/RichTextList/RichTextList';
import { ANALYTICS_LABEL } from './constants';
import { getBulletListStyle } from '@/utils/bulletListHelpers';

const MobileCardsVerticalScrollItem = ({
  sys,
  headline,
  copy,
  desktopMedia,
  mobileMedia,
  desktopAspectRatio,
  mobileAspectRatio,
  cta,
  index,
  cardCount,
  backgroundColor,
  maxMobileCardHeight,
  cardTop,
  isExpanded,
  onExpand,
  bulletStyle,
  margins,
}: CardsVerticalScrollItemMobileProps) => {
  const prevIsExpandedRef = useRef(isExpanded);
  const contentId = sys?.id ? `${sys.id}-content` : undefined;

  const cardsExpandTracking = useExpandableCardsItemToggleTracking({
    label: ANALYTICS_LABEL,
  });

  const internalClassNames = {
    wrapper: cn(
      'min-h-20 absolute left-0 right-0 overflow-hidden',
      'rounded-lg rounded-tr-lg transition-all shadow-black/25 shadow-[0_-10px_20px]',
      getColorway(backgroundColor),
      isExpanded ? getYMargins(margins) : 'my-0',
    ),
    outer: cn('flex flex-col gap-3 items-center pt-6 px-6 h-full'),
    inner: cn('flex flex-col w-full justify-between gap-4 items-center h-full pb-6', {
      'pb-24': cardCount > 2 && index < cardCount - 2,
      'pb-12': cardCount > 1 && index < cardCount - 1,
    }),
    header: {
      wrapper: cn('w-full flex justify-between items-start gap-4'),
      headline: cn('font-sans font-semibold text-heading-sm', 'col-start-1 row-start-1'),
      collapseButton: cn('flex items-center justify-between cursor-pointer w-full'),
    },
    innerContent: {
      wrapper: cn('flex flex-col gap-3 w-full'),
      copy: cn('font-serif text-body-serif text-label-serif [&_li]:my-0.5 mb-1.5'),
      cta: cn('max-w-1/2'),
    },
    media: cn('w-full'),
  };

  // useEffect to detect when the expanded state changes and track the event
  useEffect(() => {
    if (prevIsExpandedRef.current !== isExpanded) {
      cardsExpandTracking();
      trackExpandableCardItemToggled({ action: isExpanded ? 'open' : 'close' });
      prevIsExpandedRef.current = isExpanded;
    }
  }, [isExpanded, cardsExpandTracking]);

  return (
    <div
      className={internalClassNames.wrapper}
      style={{
        zIndex: 10 + index,
        transition: 'top 0.35s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.35s ease',
        height: maxMobileCardHeight ? `${maxMobileCardHeight}px` : 'auto',
        top: `${cardTop}px`,
      }}
      data-testid={`cards-vertical-scroll-item-${index}`}
      id={`cards-vertical-scroll-item-${index}`}>
      <div className={internalClassNames.outer}>
        <div className={internalClassNames.header.wrapper}>
          <button
            data-testid="mobile-vertical-card-control"
            className={internalClassNames.header.collapseButton}
            aria-controls={contentId}
            aria-expanded={isExpanded}
            onClick={onExpand}>
            <span
              data-testid="headline"
              className={cn(internalClassNames.header.headline)}
              {...liveUpdateAttrs(sys?.id, 'headline')}>
              <RichTextHeadline headline={headline} />
            </span>
            {isExpanded ? <RemoveCircle className="w-8 h-8" /> : <AddCircle className="w-8 h-8" />}
          </button>
        </div>
        <div
          className={internalClassNames.inner}
          style={{
            paddingBottom:
              cardCount > 2 && index < cardCount - 2
                ? '166px'
                : cardCount > 1 && index === cardCount - 2
                  ? '100px'
                  : '32px',
          }}>
          <div id={contentId} className={internalClassNames.innerContent.wrapper}>
            <div
              className={cn(internalClassNames.innerContent.copy)}
              {...liveUpdateAttrs(sys?.id, 'copy')}
              data-testid="card-expanded-copy">
              <RichTextList item={copy} listItemClasses="my-0" bulletListStyle={getBulletListStyle(bulletStyle)} />
            </div>
            {cta && (
              <CTA
                cta={cta}
                variant="outline"
                className={cn(internalClassNames.innerContent.cta)}
                testId="card-expanded-cta"
              />
            )}
          </div>
          <Media
            media={mobileMedia ?? desktopMedia}
            sizes="100vw"
            aspectRatio={mobileAspectRatio ?? desktopAspectRatio}
            className={internalClassNames.media}
          />
        </div>
      </div>
    </div>
  );
};

export default MobileCardsVerticalScrollItem;
